import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ScrollableTabsContainerProps {
  children: React.ReactNode;
  activeId?: string | number;
  className?: string;
}

export default function ScrollableTabsContainer({
  children,
  activeId,
  className = ''
}: ScrollableTabsContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Dragging state refs to avoid re-renders during drag
  const dragStart = useRef({ x: 0, scrollLeft: 0 });
  const isMouseDown = useRef(false);

  // Update arrow visibility based on scroll position
  const updateArrows = () => {
    const el = containerRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    
    // Show left arrow if scrolled to the right
    setShowLeftArrow(scrollLeft > 4);
    // Show right arrow if there is still content to the right
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 4);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Initial check
    updateArrows();

    // Listen to scroll events
    el.addEventListener('scroll', updateArrows);

    // Watch resize events
    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateArrows);
      observer.disconnect();
    };
  }, [children]);

  // Handle horizontal scroll with mouse wheel (even when scrolling vertically)
  const handleWheel = (e: React.WheelEvent) => {
    const el = containerRef.current;
    if (!el) return;

    // Use either deltaX or deltaY (if user is scrolling vertically, translate to horizontal)
    const scrollAmount = e.deltaY !== 0 ? e.deltaY : e.deltaX;
    
    el.scrollTo({
      left: el.scrollLeft + scrollAmount * 1.5,
      behavior: 'auto' // Instant for wheel to match standard browser physics
    });
  };

  // Drag scroll mouse down
  const handleMouseDown = (e: React.MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;

    // Only drag with primary mouse button
    if (e.button !== 0) return;

    isMouseDown.current = true;
    setIsDragging(true);
    dragStart.current = {
      x: e.pageX - el.offsetLeft,
      scrollLeft: el.scrollLeft
    };
  };

  // Drag scroll mouse move
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown.current) return;
    const el = containerRef.current;
    if (!el) return;

    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - dragStart.current.x) * 1.6; // multiplier for sensitivity
    el.scrollLeft = dragStart.current.scrollLeft - walk;
  };

  // End dragging
  const handleMouseUpOrLeave = () => {
    if (isMouseDown.current) {
      isMouseDown.current = false;
      // Small timeout to prevent click events triggering instantly on items after drag
      setTimeout(() => {
        setIsDragging(false);
      }, 50);
    }
  };

  // Scroll manually via buttons
  const scroll = (direction: 'left' | 'right') => {
    const el = containerRef.current;
    if (!el) return;

    const offset = el.clientWidth * 0.6; // scroll 60% of visible container width
    el.scrollBy({
      left: direction === 'left' ? -offset : offset,
      behavior: 'smooth'
    });
  };

  // Scroll active tab into view when activeId changes
  useEffect(() => {
    if (activeId === undefined) return;
    const el = containerRef.current;
    if (!el) return;

    // Find the active element child button
    // It will have standard data-active or isSelected attributes, but we can look up elements
    // We can also find it by selecting the element or searching for elements containing specific classes or tags
    setTimeout(() => {
      const activeEl = el.querySelector('[data-selected="true"]') as HTMLElement;
      if (activeEl) {
        // Calculate centered scroll position
        const containerWidth = el.clientWidth;
        const activeWidth = activeEl.offsetWidth;
        const activeLeft = activeEl.offsetLeft;
        const targetScrollLeft = activeLeft - (containerWidth / 2) + (activeWidth / 2);

        el.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });
      }
    }, 50);
  }, [activeId]);

  return (
    <div className="relative w-full group/scrollable flex items-center">
      {/* Left Navigation Arrow */}
      {showLeftArrow && (
        <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center pr-8 pl-1 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none">
          <button
            type="button"
            onClick={() => scroll('left')}
            className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 shadow-xs hover:shadow-sm pointer-events-auto transition-all cursor-pointer active:scale-95"
          >
            <ChevronLeft size={13} strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* Main Tabs Container */}
      <div
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        className={`${className} w-full overflow-x-auto scrollbar-none flex items-center gap-1.5 py-1 px-4 max-w-full select-none scroll-smooth ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        style={{
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Leading spacer to ensure safety margin */}
        <div className="w-1 shrink-0" />
        
        {children}

        {/* Trailing spacer to ensure safety margin */}
        <div className="w-1 shrink-0" />
      </div>

      {/* Right Navigation Arrow */}
      {showRightArrow && (
        <div className="absolute right-0 top-0 bottom-0 z-10 flex items-center pl-8 pr-1 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none">
          <button
            type="button"
            onClick={() => scroll('right')}
            className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-900 shadow-xs hover:shadow-sm pointer-events-auto transition-all cursor-pointer active:scale-95"
          >
            <ChevronRight size={13} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
}
